"use client";

import React from "react";
import Image from "next/image";
import { Title, Description } from "@/components/Common/Typography";
import {
  Award,
  Package,
  Users,
  Globe,
  MapPin,
  TrendingUp,
  Leaf,
  LucideIcon,
} from "lucide-react";
import styles from "../export.module.css";

const iconMap: Record<string, LucideIcon> = {
  Award,
  Package,
  Users,
  Globe,
  MapPin,
  TrendingUp,
  Leaf,
};

export interface HeroStat {
  num: string;
  unit: string;
  label: string;
  icon?: string;
}

export interface HeroSectionProps {
  data?: {
    subtitle?: string;
    title: string;
    description: string;
    bgImage?: string;
    stats?: HeroStat[];
  };
  locale?: string;
}

export default function HeroSection({ data }: HeroSectionProps) {
  if (!data || !data.title) return null;

  return (
    <section className={styles.hero}>
      {data.bgImage && (
        <Image
          src={data.bgImage}
          alt="Viet Thien Coffee Export"
          fill
          priority
          sizes="100vw"
          className={styles.heroBgImage}
        />
      )}

      <div className={styles.heroContainer}>
        <div className={styles.textBlock}>
          {/* Standard Typography Title */}
          <Title
            size="medium"
            as="h1"
            color="#ffffff"
            align="left"
            style={{
              marginBottom: "14px",
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
            }}
          >
            {data.title}
          </Title>

          {/* Standard Typography Description */}
          {data.description && (
            <Description
              as="p"
              size="medium"
              color="rgba(255, 255, 255, 0.95)"
              align="left"
              className={styles.heroDesc}
              style={{
                textShadow: "0 1px 5px rgba(0, 0, 0, 0.35)",
                lineHeight: 1.7,
              }}
            >
              {data.description}
            </Description>
          )}

          {/* Stats List */}
          {data.stats && data.stats.length > 0 && (
            <div className={styles.statsList}>
              {data.stats.map(({ num, unit, label, icon }, i) => {
                const Icon = (icon && iconMap[icon]) || Leaf;
                return (
                  <div className={styles.statCard} key={i}>
                    <div className={styles.statIconWrapper}>
                      <Icon size={18} />
                    </div>
                    <div className={styles.statInfo}>
                      <div className={styles.statNumberWrapper}>
                        <span className={styles.statNumber}>{num}</span>
                        <span className={styles.statUnit}>{unit}</span>
                      </div>
                      <span className={styles.statLabel}>{label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
